import { createClient } from "redis";
import hash from "object-hash";
import type { RuntimeConfig } from "nuxt/schema";
declare type Awaitable<T> = T | Promise<T>;

export { type Awaitable };

class RedisCacheService {
  public static runtimeConfig: RuntimeConfig;
  public static get Instance(): RedisCacheService {
    if (!RedisCacheService.instance) {
      RedisCacheService.instance = new RedisCacheService();
    }
    return RedisCacheService.instance;
  }

  private static instance: RedisCacheService;
  private client: any;

  private constructor() {
    const {
      redisPassword,
      redisHost,
      redisPort,
      redisDatabase,
      redisClientName
    } = RedisCacheService.runtimeConfig;
    this.client = createClient({
      url: `redis://${redisHost}:${redisPort}`,
      database: parseInt(redisDatabase + "", 10),
      password: redisPassword,
      name: redisClientName
    });

    this.client.on("error", (err: any) => {
      console.error("Error trying to connect to Redis. " + err);
      console.info("Reconnecting to Redis");
      try {
        this.client.close();
        RedisCacheService.instance = new RedisCacheService();
      } catch (err) {
        console.info(`Reconnection to Redis failed. Error: ${err}`);
      }
    });
  }

  public getHash(prefix: string, o: {}): string {
    return `${prefix}${hash(o)}`;
  }

  /**
   *
   * @param key
   * @param fn Function fired after entry has expired with key and value passed
   * @param ttl Time in seconds
   * @returns
   */
  public async cache<T>(
    key: string | number,
    fn: () => Awaitable<T>,
    ttl: number
  ): Promise<T> {
    await this.connect();

    const val = await this.getCachedEntry<T>(key);

    if (val !== undefined) return val;
    const newValue = await fn();

    this.client.set(key, JSON.stringify(newValue), {
      EX: ttl,
      NX: true
    });

    return newValue;
  }

  public async existKey(key: string | number): Promise<boolean> {
    await this.connect();
    return (await this.client.exists(key)) > 0;
  }

  /**
   * This method returns the cache entry if exists and if it is not expired
   * @param key
   * @returns
   */
  private async getCachedEntry<T>(
    key: string | number
  ): Promise<T | undefined> {
    const val = (await this.client.get(key)) as string;

    if (val) {
      return JSON.parse(val);
    }

    return;
  }

  private async connect(): Promise<void> {
    if (!this.client.isOpen) {
      await this.client.connect();
    }
  }
}

export default RedisCacheService;
